import { CSSResultGroup, html, LitElement, PropertyValues, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { returnDetails } from './return-details.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import {
  createRmaLineContext,
  updateRmaLineContext,
  draftRmaContext,
  disableMemoContext,
  disableMemo2Context,
  disableMemo3Context,
  configContext,
  NotFetchedYet,
  reasonsContext,
} from 'components/ordering/rma/components/context/rma-context.js'
import { RmaReasonDTO } from 'components/ordering/rma/api/dtos/rma-reason-dto.js'
import type { CSESelect } from '@cloudsuite/mosaic-elements'
import { RmaConfigDTO } from 'components/ordering/rma/api/dtos/rma-config-dto.js'
import { RmaDraftDTO } from 'components/ordering/rma/api/dtos/rma-draft-dto.js'
import { RmaError } from 'components/ordering/rma/api/dtos/error.js'
import { RMA_TEMPORARY_FILE_REMOVED, RMA_TEMPORARY_FILE_UPLOADED } from 'components/ordering/rma/events.js'
import 'components/ordering/rma/components/file-upload/file-upload.js'
import 'components/ordering/rma/components/file-viewer/file-viewer.js'

type ValueTarget = EventTarget & { value: string }

// Used while the RMA config has not been fetched yet.
const DEFAULT_COMMENT_MAX_LENGTH = 100

export type ReturnDetailsLine = {
  id: string | null
  productId: number
  shippedQuantity: number
  originMove: number | null
  quantity: number
  // draft fetch delivers the reason as its name; create/update deliver the id
  reason: number | string | null
  memo: string | null
  memo2: string | null
  memo3: string | null
  pictures: { name: string; identifier: string }[]
}

@customElement('csc-rma-return-details')
export class CSCRmaReturnDetails extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, returnDetails]

  @property({ attribute: 'line', type: String })
  public line = ''

  @property({ attribute: 'disable-reason-info', type: Boolean })
  public disableReasonInfo = false

  // Driven entirely by the shop's RMA configuration in trydent — not frontend-settable.
  @consume({ context: disableMemoContext, subscribe: true })
  @state()
  private memoDisabled = false

  @consume({ context: disableMemo2Context, subscribe: true })
  @state()
  private memo2Disabled = false

  @consume({ context: disableMemo3Context, subscribe: true })
  @state()
  private memo3Disabled = false

  @query('cse-select[name="reason"]')
  private reasonSelect!: CSESelect | null

  @state()
  private parsedLine: ReturnDetailsLine | null = null

  @state()
  private quantity = 0

  @state()
  private reason: number | null = null

  @state()
  private memo: string | null = null

  @state()
  private memo2: string | null = null

  @state()
  private memo3: string | null = null

  @consume({ context: reasonsContext, subscribe: true })
  @state()
  private reasons: RmaReasonDTO[] = []

  @consume({ context: configContext, subscribe: true })
  @state()
  private config: RmaConfigDTO | NotFetchedYet = new NotFetchedYet()

  private get commentMaxLength(): number {
    return this.config instanceof RmaConfigDTO
      ? this.config.maxCommentLength
      : DEFAULT_COMMENT_MAX_LENGTH
  }

  @consume({ context: createRmaLineContext, subscribe: true })
  private createRmaLine!: (payload: unknown) => Promise<void>

  @consume({ context: updateRmaLineContext, subscribe: true })
  private updateRmaLine!: (payload: unknown) => Promise<void>

  @consume({ context: draftRmaContext, subscribe: true })
  private draftRma!: RmaDraftDTO | null | NotFetchedYet

  private created = false
  private rawReason: number | string | null = null
  private reasonTouched = false

  @state() private saving = false
  @state() private saveAttempted = false
  @state() private saveError: string | ReturnType<typeof t> | null = null
  @state() private temporaryPictures: { identifier: string, name: string }[] = []

  // Temporary pictures are uploaded to the backend as soon as they're chosen
  // (see csc-rma-file-upload), so losing this list to a reload doesn't just
  // lose a text field — it orphans an already-uploaded file the user can no
  // longer see or reuse. Mirroring it to sessionStorage keeps it recoverable
  // for the lifetime of the tab, until the line is actually created.
  private temporaryPicturesKey(line: ReturnDetailsLine): string {
    return `csc-rma-temporary-pictures:${line.originMove ?? 'none'}:${line.productId}`
  }

  // Once the line exists its pictures come back on the draft, so the temporary
  // list is stale by definition. The draft refresh inside createRmaLine
  // re-renders this accordion before save() gets to clear the stored list, so
  // reading it here would show the same file twice.
  private loadTemporaryPictures(line: ReturnDetailsLine): { identifier: string, name: string }[] {
    if (line.id !== null) return []
    try {
      const raw = sessionStorage.getItem(this.temporaryPicturesKey(line))
      return raw ? (JSON.parse(raw) as { identifier: string, name: string }[]) : []
    } catch {
      return []
    }
  }

  private saveTemporaryPictures(line: ReturnDetailsLine, pictures: { identifier: string, name: string }[]): void {
    try {
      const key = this.temporaryPicturesKey(line)
      if (pictures.length === 0) sessionStorage.removeItem(key)
      else sessionStorage.setItem(key, JSON.stringify(pictures))
    } catch {
      // sessionStorage unavailable (private mode, quota) — falls back to in-memory-only, as before
    }
  }

  private onTemporaryFileUploaded = (event: Event) => {
    const { identifier, name } = (event as CustomEvent<{ identifier: string, name: string }>).detail
    this.temporaryPictures = [...this.temporaryPictures, { identifier, name }]
    if (this.parsedLine) this.saveTemporaryPictures(this.parsedLine, this.temporaryPictures)
  }

  private onTemporaryFileRemoved = (event: Event) => {
    const { identifier } = (event as CustomEvent<{ identifier: string }>).detail
    this.temporaryPictures = this.temporaryPictures.filter((picture) => picture.identifier !== identifier)
    if (this.parsedLine) this.saveTemporaryPictures(this.parsedLine, this.temporaryPictures)
  }

  override connectedCallback() {
    super.connectedCallback()
    this.addEventListener(RMA_TEMPORARY_FILE_UPLOADED, this.onTemporaryFileUploaded)
    this.addEventListener(RMA_TEMPORARY_FILE_REMOVED, this.onTemporaryFileRemoved)
  }

  override disconnectedCallback() {
    this.removeEventListener(RMA_TEMPORARY_FILE_UPLOADED, this.onTemporaryFileUploaded)
    this.removeEventListener(RMA_TEMPORARY_FILE_REMOVED, this.onTemporaryFileRemoved)
    super.disconnectedCallback()
  }

  private toErrorMessage(error: unknown): string | ReturnType<typeof t> {
    return error instanceof RmaError
      ? error.message
      : t('save_failed', { ns: 'csc-rma-return-details', default: 'Saving this line failed. Please try again.' })
  }

  protected override willUpdate(changed: PropertyValues): void {
    super.willUpdate(changed)
    if (changed.has('line')) {
      try {
        this.parsedLine = this.line ? (JSON.parse(this.line) as ReturnDetailsLine) : null
      } catch (error) {
        console.error('[csc-rma-return-details] failed to parse line attribute:', error)
        this.parsedLine = null
      }
      this.quantity = this.parsedLine?.quantity ?? 0
      this.rawReason = this.parsedLine?.reason ?? null
      this.reasonTouched = false
      this.memo = this.parsedLine?.memo ?? null
      this.memo2 = this.parsedLine?.memo2 ?? null
      this.memo3 = this.parsedLine?.memo3 ?? null
      this.created = false
      this.temporaryPictures = this.parsedLine ? this.loadTemporaryPictures(this.parsedLine) : []
    }
    // Resolve on the line change and again whenever reasons load (they arrive
    // via context, possibly after the line), until the user picks a reason.
    if ((changed.has('line') || changed.has('reasons')) && !this.reasonTouched) {
      this.reason = this.resolveReason(this.rawReason)
    }
  }

  // A saved line's reason comes back as its name (from draft fetch) rather than
  // its id, so match it against the loaded reasons to recover the id the select
  // needs; a numeric id (or its string form) passes straight through.
  private resolveReason(raw: number | string | null): number | null {
    if (raw === null || raw === '') return null
    const asNumber = typeof raw === 'number' ? raw : Number(raw)
    if (Number.isFinite(asNumber) && (this.reasons.length === 0 || this.reasons.some((r) => r.value === asNumber))) {
      return asNumber
    }
    return this.reasons.find((r) => r.text === raw || r.altText === raw)?.value ?? null
  }

  private get rmaId(): string | null {
    return this.draftRma instanceof RmaDraftDTO ? this.draftRma.id : null
  }

  private buildPayload(rmaId: string, line: ReturnDetailsLine) {
    return {
      rmaId,
      product: line.productId,
      quantity: this.quantity,
      reason: this.reason,
      memo: this.memo,
      memo2: this.memo2,
      memo3: this.memo3,
      originMove: line.originMove,
      pictures: this.temporaryPictures.map((picture) => ({ identifier: picture.identifier })),
    }
  }

  // Persist only on the explicit "Return product" action — never on field
  // edits — so a new line isn't created the moment a reason is picked and the
  // user's memos are included in the single create/update call.
  public async save() {
    if (this.saving) return
    const line = this.parsedLine
    if (!line) return

    if (line.id !== null) {
      const rmaId = this.rmaId
      if (rmaId === null) return
      this.saving = true
      this.saveError = null
      try {
        await this.updateRmaLine({ ...this.buildPayload(rmaId, line), lineId: line.id })
      } catch (error) {
        this.saveError = this.toErrorMessage(error)
      } finally {
        this.saving = false
      }
      return
    }

    // ponytail: after create, the parent re-renders the accordion with the now-existing line (id set), switching this instance to update mode; `created` just prevents a duplicate create in the gap.
    if (this.created) return
    if (this.reason === null) {
      this.saveAttempted = true
      return
    }
    if (this.quantity <= 0) {
      this.saveAttempted = true
      return
    }
    if (this.attachmentMissing) {
      this.saveAttempted = true
      return
    }
    this.saveAttempted = false
    this.saving = true
    this.created = true
    this.saveError = null
    try {
      await this.createRmaLine(this.buildPayload(this.rmaId ?? '', line))
      this.saveTemporaryPictures(line, [])
    } catch (error) {
      // release the create guard so the user can retry after a failure
      this.created = false
      this.saveError = this.toErrorMessage(error)
    } finally {
      this.saving = false
    }
  }

  // A line already saved has no "Return product" button to commit through, so
  // field edits on it autosave directly via the existing update path in save().
  private get isExistingLine(): boolean {
    return this.parsedLine?.id != null
  }

  private onQuantityChange(event: Event) {
    this.quantity = Number((event.target as ValueTarget).value) || 0
    if (this.isExistingLine) void this.save()
  }

  private onReasonChange(event: Event) {
    const value = (event.target as ValueTarget).value
    this.reason = value === '' ? null : Number(value)
    this.reasonTouched = true
    this.saveAttempted = false
    if (this.isExistingLine) void this.save()
  }

  private onMemoChange(key: 'memo' | 'memo2' | 'memo3', event: Event) {
    const value = (event.target as ValueTarget).value
    this[key] = value === '' ? null : value
    if (this.isExistingLine) void this.save()
  }

  // Clicking the button while a field is focused would blur it mid-click and
  // eat the click, so mousedown preventDefault keeps focus off the button;
  // here we blur the still-focused field first (committing its change via the
  // @change handlers) and then persist.
  private async onReturnClick() {
    let active: Element | null = document.activeElement
    while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement
    ;(active as HTMLElement | null)?.blur?.()
    await this.save()
  }

  private renderMemo(key: 'memo' | 'memo2' | 'memo3', label: ReturnType<typeof t>, maxlength: number) {
    return html`
      <cse-form-field class="rma-return-details__field">
        <cse-label slot="label" optional>
          ${label}
          <span slot="optional" data-test="rma-return-details-optional">
            ${t('optional', { ns: 'csc-rma-return-details', default: 'Optional' })}
          </span>
        </cse-label>
        <cse-textarea
          name=${key}
          maxlength=${maxlength}
          value=${this[key] ?? ''}
          @change=${(e: Event) => this.onMemoChange(key, e)}
        ></cse-textarea>
      </cse-form-field>
    `
  }

  private reasonAppliesToProduct = (reason: RmaReasonDTO): boolean =>
    !reason.tagProductIds || reason.tagProductIds.includes(this.parsedLine?.productId as number)

  private get reasonsForLine(): RmaReasonDTO[] {
    return this.reasons.filter(this.reasonAppliesToProduct)
  }

  private get selectedReason(): RmaReasonDTO | undefined {
    return this.reasons.find((r) => r.value === this.reason)
  }

  // Only hand cse-select a value it can actually match: pointing it at a reason
  // whose option is not rendered yet makes it clear the selection and emit a
  // change with '', which onReasonChange would then take for a user action.
  private get selectedReasonValue(): string {
    return this.reason !== null && this.reasonsForLine.some((r) => r.value === this.reason)
      ? String(this.reason)
      : ''
  }

  private get attachmentRequired(): boolean {
    return this.selectedReason?.requiredAttachment ?? false
  }

  private get attachmentMissing(): boolean {
    if (!this.attachmentRequired) return false
    const pictures = this.parsedLine?.pictures ?? []
    return pictures.length === 0 && this.temporaryPictures.length === 0
  }

  private renderReasonInfo() {
    if (this.disableReasonInfo) return nothing
    if (!this.selectedReason) return nothing
    const { altText, description } = this.selectedReason
    if (!altText && !description) return nothing
    return html`
      <cse-alert variant="info">
        ${altText ? html`<strong>${altText}</strong>` : nothing}
        ${description ?? nothing}
      </cse-alert>
    `
  }

  private renderUpload(lineId: string | null) {
    const pictures = this.parsedLine?.pictures ?? []
    const hasFiles = pictures.length > 0 || this.temporaryPictures.length > 0
    return html`
      <div class="rma-return-details__upload">
        ${hasFiles
          ? html`<ul class="rma-return-details__files">
              ${pictures.map(
                (picture) => html`<csc-rma-file-viewer
                  line-id=${lineId ?? ''}
                  identifier=${picture.identifier}
                  name=${picture.name}
                ></csc-rma-file-viewer>`
              )}
              ${this.temporaryPictures.map(
                (picture) => html`<csc-rma-file-viewer
                  ?temporary=${true}
                  identifier=${picture.identifier}
                  name=${picture.name}
                ></csc-rma-file-viewer>`
              )}
            </ul>`
          : nothing}
        <csc-rma-file-upload line-id=${lineId ?? ''} ?required=${this.attachmentRequired}></csc-rma-file-upload>
        ${this.saveAttempted && this.attachmentMissing
          ? html`<cse-text
              role="alert"
              class="rma-return-details__error"
              data-test="rma-return-details-attachment-required"
            >
              ${t('attachment_required', { ns: 'csc-rma-return-details', default: 'Please add a photo of the product/issue' })}
            </cse-text>`
          : nothing}
      </div>
    `
  }

  // cse-select resolves its selection against the option list it snapshotted in
  // the last setOptions(). When the reasons arrive after the line, its own
  // updated() matches the new value against that stale (empty) list, clears the
  // selection and emits a change with '' — which would land in onReasonChange
  // and wipe the reason. Re-snapshotting here runs first and makes the match
  // succeed, so no spurious change is emitted.
  protected override updated(changed: PropertyValues): void {
    super.updated(changed)
    this.reasonSelect?.setOptions()
  }

  override render() {
    if (!this.parsedLine) return html`<div class="rma-return-details" data-test="rma-return-details"></div>`
    const line = this.parsedLine
    return html`
      <div class="rma-return-details" data-test="rma-return-details">
        <cse-divider orientation="horizontal"></cse-divider>
        <div class="rma-return-details__quantity-row">
          <cse-form-field class="rma-return-details__field rma-return-details__field--quantity">
            <cse-label slot="label">
              ${t('quantity', { ns: 'csc-rma-return-details', default: 'Quantity' })}
            </cse-label>
            <div class="rma-return-details__quantity">
              <cse-text-input
                class="rma-return-details__quantity-input"
                name="quantity"
                type="number"
                min="1"
                max=${line.shippedQuantity}
                value=${String(this.quantity)}
                @change=${(e: Event) => this.onQuantityChange(e)}
              ></cse-text-input>
              ${line.shippedQuantity > 0
                ? html`<cse-text variant="body-s" data-test="rma-return-details-shipped-quantity">
                    /${line.shippedQuantity}
                  </cse-text>`
                : nothing}
            </div>
            ${this.saveAttempted && this.reason !== null && this.quantity <= 0
              ? html`<span slot="invalid-text" class="rma-return-details__error">${t('quantity_required', { ns: 'csc-rma-return-details', default: 'Please enter a quantity' })}</span>`
              : nothing}
          </cse-form-field>
          <cse-form-field class="rma-return-details__field rma-return-details__field--reason">
            <cse-label slot="label">
              ${t('reason', { ns: 'csc-rma-return-details', default: 'Reason' })}
            </cse-label>
            <cse-select
              name="reason"
              value=${this.selectedReasonValue}
              ?invalid=${this.saveAttempted && this.reason === null}
              @change=${(e: Event) => this.onReasonChange(e)}
            >
              ${this.reasonsForLine.map(
                (r) => html`<cse-option value=${String(r.value)}>${r.text}</cse-option>`
              )}
            </cse-select>
            ${this.saveAttempted && this.reason === null
              ? html`<span slot="invalid-text" class="rma-return-details__error">${t('reason_required', { ns: 'csc-rma-return-details', default: 'Please select a reason' })}</span>`
              : nothing}
          </cse-form-field>
        </div>
        ${this.renderReasonInfo()}
        <div class="rma-return-details__memo-row">
          ${this.memoDisabled ? nothing : this.renderMemo('memo', t('memo', { ns: 'csc-rma-return-details', default: 'Serial number' }), 100)}
          ${this.memo2Disabled ? nothing : this.renderMemo('memo2', t('memo_two', { ns: 'csc-rma-return-details', default: 'Comment' }), this.commentMaxLength)}
        </div>
        ${this.memo3Disabled ? nothing : this.renderMemo('memo3', t('memo_three', { ns: 'csc-rma-return-details', default: 'Additional information' }), 500)}
        <div class="rma-return-details__actions">
          ${this.renderUpload(line.id)}
          ${this.isExistingLine
            ? nothing
            : html`<cse-button
                class="rma-return-details__return"
                variant="primary"
                ?loading=${this.saving}
                @mousedown=${(e: Event) => e.preventDefault()}
                @click=${() => void this.onReturnClick()}
              >
                ${t('return_product', { ns: 'csc-rma-return-details', default: 'Return product' })}
              </cse-button>`}
        </div>
        ${this.saveError
          ? html`<span class="rma-return-details__error" role="alert">${this.saveError}</span>`
          : nothing}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-return-details': CSCRmaReturnDetails
  }
}
